import {
  CloseOutlined,
  CloudUploadOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Steps,
  Tabs,
  Tag,
  Tooltip,
  Upload,
} from 'antd'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { blue } from '../../../color'
import { AvatarHasName } from '../../../components/Common/Avatar'
import SelectPeoplePopup from '../../../components/SelectPeoplePopup/SelectPeoplePopup'
import { ASSIGNEE_TYPE, DATE_FORMAT_LIST } from '../../../constants'
import loadingAnimationStore from '../../../stores/loadingAnimationStore'
import utils from '../../../utils'
import {
  CustomStepIcon,
  ModalCreateSignDocWrapper,
  StepsWrapper,
} from '../../Documents/InternalDocumentSignDocPage/PopupCreateSignDoc/PopupCreateSignDocStyled'

const { Option } = Select
const { Step } = Steps
const { TabPane } = Tabs

const PopupElectronicReleaseForm = props => {
  const {
    selectPeopleStore,
    isVisiblePopupElectronicRelease,
    workStore,
    handleCloseElectronicRelease,
    fileStore,
    authenticationStore,
  } = props

  const { workDetail, commentWorkList, groupVanThu } = workStore

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    clearSelectUserData,
  } = selectPeopleStore

  const [form] = Form.useForm()
  const EDITOR_REF = useRef()

  const { currentUser } = authenticationStore

  const [departmentApproverList, setDepartmentApproverList] = useState([])
  const [formApproverList, setFormApproverList] = useState([])
  const [leaderApproverList, setLeaderApproverList] = useState([])
  const [digitalSignerList, setDigitalSignerList] = useState([])
  const [releaserList, setReleaserList] = useState([])
  const [permission, setPermission] = useState([
    ASSIGNEE_TYPE.DEPARTMENT_APPROVER,
    ASSIGNEE_TYPE.FORM_APPROVER,
    ASSIGNEE_TYPE.LEADER_APPROVER,
    ASSIGNEE_TYPE.DIGITAL_SIGNER,
    ASSIGNEE_TYPE.RELEASER,
  ])
  const [stepsData, setStepsData] = useState([])

  const [electronicDocumentList, setElectronicDocumentList] = useState([])
  const [attachmentList, setAttachmentList] = useState([])
  const [annexAttachmentList, setAnnexAttachmentList] = useState([])
  const [attachments, setAttachments] = useState([])

  // Build steps from lists
  useEffect(() => {
    const allSteps = [
      {
        key: 1,
        title: 'Tạo dự thảo',
        userList: currentUser
          ? [
              {
                image_id: currentUser.imageId,
                full_name: currentUser.name_lowercase,
              },
            ]
          : [],
        allowDeleted: false,
      },
      {
        key: 2,
        title: 'Trưởng phòng duyệt',
        userList: departmentApproverList || [],
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.DEPARTMENT_APPROVER,
        permission: 'DEPARTMENT_APPROVER',
      },
      {
        key: 3,
        title: 'Duyệt thể thức',
        userList: formApproverList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.FORM_APPROVER,
        permission: 'FORM_APPROVER',
      },
      {
        key: 4,
        title: 'Lãnh đạo duyệt',
        userList: leaderApproverList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.LEADER_APPROVER,
        permission: 'LEADER_APPROVER',
      },
      {
        key: 5,
        title: 'Ký số điện tử',
        userList: digitalSignerList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.DIGITAL_SIGNER,
        permission: 'DIGITAL_SIGNER',
      },
      {
        key: 6,
        title: 'Phát hành',
        userList: releaserList || [],
        type: 'approval',
        allowDeleted: false,
        assignee_type: ASSIGNEE_TYPE.RELEASER,
        permission: 'RELEASER',
      },
    ]
    setStepsData(
      allSteps.filter(
        step => !step.permission || permission.includes(step.permission)
      )
    )
  }, [
    releaserList,
    currentUser,
    departmentApproverList,
    formApproverList,
    leaderApproverList,
    digitalSignerList,
    permission,
  ])

  // Collect attachments from work detail + comments
  useEffect(() => {
    const fileListFromComment = []
    commentWorkList &&
      commentWorkList.forEach(item => {
        if (!item.files || !Array.isArray(item.files)) return
        item.files.forEach(file => fileListFromComment.push(file))
      })
    const attachmentsDetail = workDetail.attachments || []
    setAttachments([...toJS(attachmentsDetail), ...toJS(fileListFromComment)])
  }, [workDetail, commentWorkList])

  useEffect(() => {
    ;(async () => {
      await clearSelectUserData()
    })()
  }, [selectPeopleStore])

  // Sync user selections into lists
  useEffect(() => {
    setDepartmentApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.DEPARTMENT_APPROVER] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.DEPARTMENT_APPROVER] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.DEPARTMENT_APPROVER] || []),
    ])
    setFormApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.FORM_APPROVER] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.FORM_APPROVER] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.FORM_APPROVER] || []),
    ])
    setLeaderApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.LEADER_APPROVER] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.LEADER_APPROVER] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.LEADER_APPROVER] || []),
    ])
    setDigitalSignerList([
      ...(selectUserData[ASSIGNEE_TYPE.DIGITAL_SIGNER] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.DIGITAL_SIGNER] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.DIGITAL_SIGNER] || []),
    ])
    setReleaserList([
      ...(selectUserData[ASSIGNEE_TYPE.RELEASER] || []),
      ...(selectDepartmentData[ASSIGNEE_TYPE.RELEASER] || []),
      ...(selectGroupData[ASSIGNEE_TYPE.RELEASER] || []),
    ])
  }, [selectUserData, selectDepartmentData, selectGroupData])

  // Populate file lists from attachments
  useEffect(() => {
    setElectronicDocumentList(
      toJS(attachments)
        .filter(file =>
          ['pdf', 'doc', 'docx'].includes(
            utils.getExtensionFile(file.file_name)
          )
        )
        .map(item => ({ ...item, checked: false }))
    )
    setAttachmentList(
      toJS(attachments).map(item => ({
        ...item,
        disabled: false,
        checked: false,
      }))
    )
    setAnnexAttachmentList(
      toJS(attachments).map(item => ({
        ...item,
        disabled: false,
        checked: false,
      }))
    )
  }, [attachments])

  // Auto-set group van thu
  useEffect(() => {
    if (!groupVanThu || !isVisiblePopupElectronicRelease) return
    selectPeopleStore.setSelectGroupData({
      [ASSIGNEE_TYPE.HANDLER]: [],
      [ASSIGNEE_TYPE.COMBINER]: [],
      [ASSIGNEE_TYPE.FOLLOWER]: [
        {
          assignee_type: ASSIGNEE_TYPE.GROUP,
          full_name: groupVanThu.name,
          id: groupVanThu.id,
          permission: ASSIGNEE_TYPE.FOLLOWER,
        },
      ],
      [ASSIGNEE_TYPE.LEADER]: [],
    })
  }, [groupVanThu, isVisiblePopupElectronicRelease])

  useEffect(() => {
    if (isVisiblePopupElectronicRelease) {
      workStore.getGroupVanThu()
    }
  }, [isVisiblePopupElectronicRelease])

  // Validate electronic doc selection
  useEffect(() => {
    if (!isVisiblePopupElectronicRelease) return
    if (!electronicDocumentList.some(item => item.checked)) {
      form.setFieldsValue({ electronic_id: undefined })
    } else {
      form.setFieldsValue({ electronic_id: 'selected' })
    }
    const electronicDocumentChecked = electronicDocumentList.find(
      item => item.checked
    )
    setAttachmentList(prev =>
      prev.map(item => ({
        ...item,
        disabled: electronicDocumentChecked
          ? electronicDocumentChecked.file_id === item.file_id
          : false,
      }))
    )
  }, [electronicDocumentList])

  const onFinish = async value => {
    const code_work = workDetail?.code

    const electronicFile = electronicDocumentList.find(item => item.checked)
    if (!electronicFile) {
      return message.error('Vui lòng chọn tài liệu trình ký!')
    }

    const assigneesList = stepsData
      .filter(el => el.key !== 1)
      .map(el => ({
        assignee_code: el.userList[0] ? el.userList[0].id : '',
        assignee_type: el.userList[0] ? el.userList[0].assignee_type : 'USER',
        permission: el.permission,
      }))

    const permissionStr = stepsData
      .filter(el => el.key !== 1)
      .map(el => el.permission)
      .join(',')

    let idElectronicFile
    if (!electronicFile.originFileObj) {
      idElectronicFile = electronicFile.file_id
    } else {
      try {
        loadingAnimationStore.showSpinner(true)
        const formDataElectronic = new FormData()
        formDataElectronic.append('file', electronicFile.originFileObj)
        const { data } = await fileStore.uploadFile(formDataElectronic)
        idElectronicFile = data.file_id
      } catch (err) {
        console.log(err)
        message.error(err?.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    }

    const checkedAttachments = attachmentList.filter(
      item => item.checked && !item.disabled
    )
    const attachmentUploaded = checkedAttachments
      .filter(item => !item.originFileObj)
      .map(item => item.file_id)
    const uploadAttachmentList = checkedAttachments
      .filter(item => item.originFileObj)
      .map(item => item.originFileObj)

    const batchUploadArr = uploadAttachmentList.map(file => {
      const formData = new FormData()
      formData.append('file', file)
      return fileStore.uploadFile(formData)
    })

    try {
      loadingAnimationStore.showSpinner(true)
      const response = await Promise.all(batchUploadArr)
      const uploadedFiles = response.map(res => res.data.file_id)
      const dataSubmit = {
        ...value,
        electronic_id: undefined,
        attachments: [...attachmentUploaded, ...uploadedFiles],
        file_id: idElectronicFile,
        assignees: assigneesList,
        parent_id: code_work,
        content: EDITOR_REF.current?.editor?.getData() || '',
        related_documents: [],
        permission: permissionStr,
      }
      await workStore.createWork(dataSubmit)
      const responseDetail = await workStore.getWorkDetail(code_work)
      await workStore.getListSubWork(responseDetail.data.code)
      handleCancel()
      message.success('Tạo công việc thành công!')
    } catch (err) {
      console.log(err)
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleCancel = () => {
    handleCloseElectronicRelease()
    form.resetFields()
    selectPeopleStore.clearSelectUserData()
    selectPeopleStore.clearSelectDepartmentData()
    selectPeopleStore.clearSelectGroupData()
    selectPeopleStore.setSelectGroupData({
      [ASSIGNEE_TYPE.HANDLER]: [],
      [ASSIGNEE_TYPE.COMBINER]: [],
      [ASSIGNEE_TYPE.FOLLOWER]: groupVanThu
        ? [
            {
              assignee_type: ASSIGNEE_TYPE.GROUP,
              full_name: groupVanThu.name,
              id: groupVanThu.id,
              permission: ASSIGNEE_TYPE.FOLLOWER,
            },
          ]
        : [],
      [ASSIGNEE_TYPE.LEADER]: [],
    })
    setElectronicDocumentList(
      toJS(attachments)
        .filter(file =>
          ['pdf', 'doc', 'docx'].includes(
            utils.getExtensionFile(file.file_name)
          )
        )
        .map(item => ({ ...item, checked: false }))
    )
    setAttachmentList(
      toJS(attachments).map(item => ({
        ...item,
        disabled: false,
        checked: false,
      }))
    )
    setAnnexAttachmentList(
      toJS(attachments).map(item => ({
        ...item,
        disabled: false,
        checked: false,
      }))
    )
  }

  const handleRemoveStep = useCallback(
    key => {
      const newStepsData = stepsData.filter(step => step.key !== key)
      setStepsData(newStepsData)
      setPermission(
        newStepsData.filter(el => el.key !== 1).map(el => el.permission)
      )
    },
    [stepsData]
  )

  const existingStepKeys = useCallback(() => stepsData.map(step => step.key), [
    stepsData,
  ])

  const allSteps = [
    {
      key: 1,
      title: 'Tạo dự thảo',
      userList: currentUser
        ? [
            {
              image_id: currentUser.imageId,
              full_name: currentUser.name_lowercase,
            },
          ]
        : [],
      allowDeleted: false,
    },
    {
      key: 2,
      title: 'Trưởng phòng duyệt',
      userList: departmentApproverList || [],
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.DEPARTMENT_APPROVER,
      permission: 'DEPARTMENT_APPROVER',
    },
    {
      key: 3,
      title: 'Duyệt thể thức',
      userList: formApproverList || [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.FORM_APPROVER,
      permission: 'FORM_APPROVER',
    },
    {
      key: 4,
      title: 'Lãnh đạo duyệt',
      userList: leaderApproverList || [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.LEADER_APPROVER,
      permission: 'LEADER_APPROVER',
    },
    {
      key: 5,
      title: 'Ký số điện tử',
      userList: digitalSignerList || [],
      type: 'approval',
      allowDeleted: true,
      assignee_type: ASSIGNEE_TYPE.DIGITAL_SIGNER,
      permission: 'DIGITAL_SIGNER',
    },
    {
      key: 6,
      title: 'Phát hành',
      userList: releaserList || [],
      type: 'approval',
      allowDeleted: false,
      assignee_type: ASSIGNEE_TYPE.RELEASER,
      permission: 'RELEASER',
    },
  ]

  const handleAddStep = useCallback(
    key => {
      if (!key) return
      const stepToAdd = allSteps.find(step => step.key === key)
      if (stepToAdd) {
        setStepsData(prev => {
          const newSteps = [...prev, stepToAdd].sort((a, b) => a.key - b.key)
          setPermission(
            newSteps.filter(el => el.key !== 1).map(el => el.permission)
          )
          return newSteps
        })
      }
    },
    [allSteps]
  )

  const onChangeElectronicDocumentCheckBox = info => {
    setElectronicDocumentList(prev =>
      prev
        .map(item => ({ ...item, checked: false }))
        .map(item =>
          item.file_id === info.target.value
            ? { ...item, checked: info.target.checked }
            : item
        )
    )
  }

  const onChangeAttachmentCheckBox = info => {
    setAttachmentList(prev =>
      prev.map(item =>
        item.file_id === info.target.value
          ? { ...item, checked: info.target.checked }
          : item
      )
    )
  }

  const onChangeAnnexCheckBox = info => {
    setAnnexAttachmentList(prev =>
      prev.map(item =>
        item.file_id === info.target.value
          ? { ...item, checked: info.target.checked }
          : item
      )
    )
  }

  const handleChangeElectronicDoc = info => {
    const originElectronicList = toJS(attachments)
      .filter(file =>
        ['pdf', 'doc', 'docx'].includes(utils.getExtensionFile(file.file_name))
      )
      .map(item => ({ ...item, checked: false }))
    const newFile = info.file
    const newList = [
      {
        file_id: newFile.uid,
        file_name: newFile.name,
        checked: true,
        originFileObj: newFile,
      },
      ...originElectronicList,
    ]
    setElectronicDocumentList(newList)
  }

  const handleChangeAttachment = info => {
    const newItems = info.fileList
      .filter(f => !attachmentList.find(a => a.file_id === f.uid))
      .map(item => ({
        file_id: item.uid,
        file_name: item.name,
        originFileObj: item.originFileObj,
        checked: true,
        disabled: false,
      }))
    if (newItems.length > 0) {
      setAttachmentList(prev => [...newItems, ...prev])
    }
  }

  const renderTitleStep = level => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <span>{level.title}</span>
      {level.key !== 1 && (
        <Tooltip title="Chọn người dùng" color={blue}>
          <Button
            size="small"
            shape="circle"
            icon={<UserOutlined style={{ color: blue }} />}
            onClick={() =>
              handleOpenSelectUserDepartmentPopup(level.assignee_type)
            }
          />
        </Tooltip>
      )}
    </div>
  )

  const renderDescStep = level => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: 14,
        marginBottom: 14,
      }}>
      {level.userList[0] ? (
        <Tag
          className={'tag-selected'}
          color={'blue'}
          closable={level.key !== 1}
          onClose={() =>
            selectPeopleStore.handleRemoveSelect(level.userList[0])
          }>
          <AvatarHasName
            imgId={level.userList[0]?.image_id}
            size={22}
            name={level.userList[0]?.full_name}
          />
        </Tag>
      ) : (
        <Tag style={{ borderRadius: 10 }}>Chưa gán người!</Tag>
      )}
    </div>
  )

  const renderFileChecklist = (fileList, onChangeFn) => (
    <div style={{ padding: '8px 0', minHeight: 80 }}>
      {fileList.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 16px',
            border: '1.5px dashed #d9d9d9',
            borderRadius: 8,
            color: '#aaa',
            gap: 8,
          }}>
          <CloudUploadOutlined style={{ fontSize: 32, color: '#bbb' }} />
          <span>Không có tài liệu nào trong công việc</span>
        </div>
      ) : (
        <Checkbox.Group
          value={fileList
            .filter(item => item.checked)
            .map(item => item.file_id)}
          style={{ width: '100%' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {fileList.map(el => (
              <Checkbox
                onChange={onChangeFn}
                key={el.file_id}
                value={el.file_id}
                disabled={el.disabled}>
                {el.file_name}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      )}
    </div>
  )

  return (
    <ModalCreateSignDocWrapper
      visible={isVisiblePopupElectronicRelease}
      footer={null}
      closable={false}
      title={'Tạo mới công việc'}
      width={1000}
      style={{ top: 60 }}>
      <div className="inner-form-card">
        <Form
          onFinish={onFinish}
          form={form}
          layout="vertical"
          initialValues={{
            work_type: 'ELECTRONIC',
            title: 'Tạo mới công việc',
            start_date: moment(),
          }}>
          {/* Hidden field */}
          <Form.Item style={{ display: 'none' }} name={'work_type'}>
            <Input />
          </Form.Item>

          <Form.Item
            label={'Tiêu đề:'}
            name={'title'}
            rules={[
              { required: true, message: 'Vui lòng nhập tiêu đề' },
              { max: 500, message: 'Tiêu đề không được dài quá 500 ký tự!' },
            ]}>
            <Input placeholder={'Nhập tiêu đề...'} />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label={'Ngày bắt đầu:'}
                required
                rules={[
                  { required: true, message: 'Vui lòng chọn ngày bắt đầu!' },
                ]}
                name={'start_date'}>
                <DatePicker
                  format={DATE_FORMAT_LIST}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={'Ngày kết thúc:'} name={'due_date'}>
                <DatePicker
                  format={DATE_FORMAT_LIST}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Tabs for file sections */}
          <Form.Item
            name={'electronic_id'}
            rules={[
              { required: true, message: 'Vui lòng chọn tài liệu trình ký!' },
            ]}>
            <Tabs defaultActiveKey="sign" type="line">
              <TabPane
                tab={
                  <span>
                    Tài liệu trình ký <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                key="sign">
                <Upload
                  accept={'.pdf,.doc,.docx'}
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleChangeElectronicDoc}>
                  <Button
                    icon={<UploadOutlined />}
                    style={{ marginBottom: 10 }}
                    htmlType="button">
                    Chọn tài liệu trình ký khác
                  </Button>
                </Upload>
                {renderFileChecklist(
                  electronicDocumentList,
                  onChangeElectronicDocumentCheckBox
                )}
              </TabPane>
              <TabPane tab="Tài liệu đính kèm" key="attachment">
                <Upload
                  multiple
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleChangeAttachment}>
                  <Button
                    icon={<UploadOutlined />}
                    style={{ marginBottom: 10 }}
                    htmlType="button">
                    Chọn thêm tài liệu đính kèm
                  </Button>
                </Upload>
                {renderFileChecklist(
                  attachmentList,
                  onChangeAttachmentCheckBox
                )}
              </TabPane>
            </Tabs>
          </Form.Item>

          {/* Step selector */}
          <div style={{ marginBottom: 8 }}>
            <Select
              placeholder="Chọn bước để thêm"
              value={null}
              onChange={value => handleAddStep(value)}
              style={{ width: 300 }}>
              {allSteps
                .filter(item => item.allowDeleted)
                .map(step => (
                  <Option
                    key={step.key}
                    value={step.key}
                    disabled={existingStepKeys().includes(step.key)}>
                    {step.title}
                  </Option>
                ))}
            </Select>
          </div>

          <StepsWrapper count={stepsData.length} i={4}>
            <Steps
              status="process"
              className={'steps'}
              labelPlacement={'vertical'}
              size={'small'}>
              {stepsData.map(level => (
                <Step
                  key={level.key}
                  title={renderTitleStep(level)}
                  description={renderDescStep(level)}
                  icon={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CustomStepIcon>{level.key}</CustomStepIcon>
                      {level.allowDeleted && (
                        <CloseOutlined
                          style={{ marginLeft: 6, fontSize: 12 }}
                          onClick={() => handleRemoveStep(level.key)}
                        />
                      )}
                    </div>
                  }
                />
              ))}
            </Steps>
          </StepsWrapper>

          {/* Footer buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0',
            }}>
            <Button onClick={handleCancel} style={{ width: 140, height: 40 }}>
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{ width: 140, height: 40 }}>
              Tạo mới
            </Button>
          </div>
        </Form>
      </div>

      <SelectPeoplePopup
        onlyOneUser={true}
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </ModalCreateSignDocWrapper>
  )
}

PopupElectronicReleaseForm.propTypes = {}

export default inject(
  'selectPeopleStore',
  'workStore',
  'fileStore',
  'authenticationStore'
)(observer(PopupElectronicReleaseForm))
